import { useCallback, useEffect, useState } from 'react'
import { clearCreatorMemories, deleteCreatorMemory, getCreatorMemories, updateCreatorMemory } from '../api/data'
import { ApiError } from '../api/client'
import { PageHeader, StatusMessage } from '../components/AppShell'
import type { CreatorMemory, CreatorMemoryCategory, LoadState } from '../types/api'

const CATEGORY_LABELS: Record<CreatorMemoryCategory, string> = {
  user_preference: '用户偏好',
  creator_positioning: '创作者定位',
  long_term_goal: '长期目标',
  fixed_constraint: '固定约束',
}

const CATEGORY_ICONS: Record<CreatorMemoryCategory, string> = {
  user_preference: 'ri-heart-3-line',
  creator_positioning: 'ri-user-star-line',
  long_term_goal: 'ri-flag-2-line',
  fixed_constraint: 'ri-lock-2-line',
}

function formatTime(value?: string) {
  if (!value) return '更新时间未知'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN', { hour12: false })
}

function pointsFromText(value: string) {
  return value.split(/\r?\n/).map(item => item.trim()).filter(Boolean)
}

function MemoryCard({ item, onSaved, onDeleted }: { item: CreatorMemory; onSaved: (value: CreatorMemory) => void; onDeleted: (key: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [category, setCategory] = useState<CreatorMemoryCategory>(item.category)
  const [content, setContent] = useState(item.content.join('\n'))
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState('')

  useEffect(() => {
    setCategory(item.category)
    setContent(item.content.join('\n'))
  }, [item])

  const save = async () => {
    const points = pointsFromText(content)
    if (!points.length) { setNotice('至少保留一条记忆事实'); return }
    setSaving(true)
    setNotice('')
    try {
      const result = await updateCreatorMemory(item.key, { category, content: points })
      onSaved(result)
      setEditing(false)
    } catch (error) {
      setNotice(error instanceof Error ? error.message : '记忆更新失败')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!window.confirm('确定删除这条长期记忆吗？删除后会立即从 Creator Memory Store 中移除。')) return
    setSaving(true)
    setNotice('')
    try {
      await deleteCreatorMemory(item.key)
      onDeleted(item.key)
    } catch (error) {
      setNotice(error instanceof Error ? error.message : '记忆删除失败')
      setSaving(false)
    }
  }

  return <article className={`card creator-memory-card ${editing ? 'is-editing' : ''}`}>
    <header className="creator-memory-card-head">
      <div className="creator-memory-card-title"><span className="creator-memory-icon"><i className={CATEGORY_ICONS[item.category]} aria-hidden="true" /></span><div><span className="badge badge-muted">{CATEGORY_LABELS[item.category]}</span><h2>{item.key}</h2></div></div>
      <time dateTime={item.updated_at}>{formatTime(item.updated_at)}</time>
    </header>
    {editing ? <div className="creator-memory-editor"><label><span>记忆类型</span><select value={category} onChange={event => setCategory(event.target.value as CreatorMemoryCategory)}>{Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><label><span>长期有效事实</span><textarea value={content} onChange={event => setContent(event.target.value)} rows={Math.max(3, Math.min(8, content.split('\n').length + 1))} placeholder="每行一条独立事实" /><small>每行一条；只保留偏好、定位、目标或固定约束。</small></label></div> : <ul className="creator-memory-points">{item.content.map((point, index) => <li key={`${item.key}-${index}`}>{point}</li>)}</ul>}
    {notice && <p className="creator-memory-card-notice" role="alert">{notice}</p>}
    <footer className="creator-memory-card-foot"><span>Store key · {item.key}</span>{editing ? <div><button className="btn btn-sm" disabled={saving} onClick={() => { setEditing(false); setNotice('') }}>取消</button><button className="btn btn-primary btn-sm" disabled={saving} onClick={() => void save()}>{saving ? '保存中…' : '保存'}</button></div> : <div><button className="btn btn-sm" disabled={saving} onClick={() => setEditing(true)}>编辑</button><button className="btn btn-sm danger" disabled={saving} onClick={() => void remove()}>删除</button></div>}</footer>
  </article>
}

export function MemoryPage() {
  const [state, setState] = useState<LoadState>('loading')
  const [items, setItems] = useState<CreatorMemory[]>([])
  const [notice, setNotice] = useState('')

  const load = useCallback(async () => {
    setState('loading')
    setNotice('')
    try {
      const result = await getCreatorMemories()
      setItems(result || [])
      setState(result?.length ? 'ready' : 'empty')
    } catch (error) {
      setState(error instanceof ApiError && error.unavailable ? 'unavailable' : 'error')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const clear = async () => {
    if (!items.length || !window.confirm('确定清空当前账号的全部长期记忆吗？此操作不可恢复。')) return
    try {
      const result = await clearCreatorMemories()
      setItems([])
      setState('empty')
      setNotice(`已清空 ${result.deleted} 条长期记忆`)
    } catch (error) {
      setNotice(error instanceof Error ? error.message : '清空记忆失败')
    }
  }

  const openAi = () => {
    sessionStorage.setItem('biliup.ai.pending_prompt', '我想更新或讨论我的长期创作偏好')
    location.hash = '#/ai'
  }

  return <>
    <PageHeader eyebrow="CREATOR MEMORY" title="创作者记忆" description="管理当前账号被确认保存的长期偏好、定位、目标和固定约束。" />
    {notice && <div className="inline-notice" role="status">{notice}</div>}
    {state === 'loading' || state === 'error' || state === 'unavailable' ? <StatusMessage state={state === 'loading' ? 'loading' : state} onRetry={load} /> : state === 'empty' ? <section className="card creator-memory-empty"><span className="creator-memory-empty-icon"><i className="ri-inbox-archive-line" aria-hidden="true" /></span><h2>还没有已保存的长期记忆</h2><p>你可以在 Biliup AI 中明确说“记住……”或“以后都按照这个规则”，再由 AI 判断并保存。</p><button className="btn btn-primary" onClick={openAi}>去 AI 对话</button></section> : <><div className="creator-memory-summary"><div><span className="eyebrow">LONG-TERM CONTEXT</span><strong>{items.length}</strong><span>条当前账号记忆</span></div><div><span>记忆只保存可长期复用的信息</span><button className="text-button danger" onClick={() => void clear()}>清空全部</button></div></div><section className="creator-memory-list" aria-label="已保存的长期记忆">{items.map(item => <MemoryCard key={item.key} item={item} onSaved={value => setItems(current => current.map(existing => existing.key === value.key ? value : existing))} onDeleted={key => { const next = items.filter(item => item.key !== key); setItems(next); if (!next.length) setState('empty') }} />)}</section></>}
  </>
}
